import React from 'react';
import { RefreshCw, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-20">
      <SEOHead
        title="Refund & Cancellation Policy | Alden Technologies"
        description="Refund and cancellation policy for orders, advances and services at Alden Technologies."
        path="/refund-policy"
      />
      <Breadcrumbs items={[{ label: 'Refund Policy' }]} />
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-12">
          <div className="text-center mb-8 md:mb-12">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Refund & Cancellation Policy</h1>
            <p className="text-gray-600">Last Updated: May 2025</p>
          </div>

          <div className="prose prose-lg max-w-none space-y-6">
            <section>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-4 mb-6">
                <p className="text-gray-800 font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  Important Notice
                </p>
                <p className="text-gray-700 mt-2">
                  Due to the customized and made-to-order nature of industrial machinery and bottling plants, refunds and cancellations are subject to specific terms outlined below.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <XCircle className="w-6 h-6 text-red-600" />
                Cancellation Policy
              </h2>
              
              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Before Manufacturing Starts:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Within 7 days of order confirmation:</strong> Cancellation allowed with 10% deduction from advance payment (administrative & processing charges)</li>
                <li><strong>8-15 days after order confirmation:</strong> Cancellation allowed with 25% deduction from advance payment</li>
                <li><strong>After 15 days:</strong> Cancellation allowed with 40% deduction as raw materials may have been procured</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">After Manufacturing Starts:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Once customized manufacturing begins, cancellation is <strong>not permitted</strong></li>
                <li>Customer may transfer the order to another party with our written consent</li>
                <li>Storage charges of ₹5,000 per month applicable if customer delays taking delivery beyond agreed timeline</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">After Dispatch/Installation:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>No cancellation or refund possible after equipment dispatch</li>
                <li>Customer responsible for full payment as per agreed terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <RefreshCw className="w-6 h-6 text-blue-600" />
                Refund Policy
              </h2>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-4">Eligible for Refund:</h3>
              <div className="bg-green-50 p-4 rounded-lg mb-4">
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Manufacturing Defects:</strong> If major defects are found during installation that cannot be rectified, full refund after equipment return</li>
                  <li><strong>Non-Delivery:</strong> If we fail to deliver within 120 days of agreed delivery date (force majeure excluded)</li>
                  <li><strong>Wrong Product Delivered:</strong> Completely different product delivered than ordered</li>
                  <li><strong>Duplicate Payment:</strong> If payment is charged twice mistakenly</li>
                </ul>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-6">Not Eligible for Refund:</h3>
              <div className="bg-red-50 p-4 rounded-lg mb-4">
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Change of mind or business plan after order confirmation</li>
                  <li>Minor specification variations within industry standards (±5% capacity variation is acceptable)</li>
                  <li>Damage during customer's transportation or improper installation by unauthorized persons</li>
                  <li>Issues arising from inadequate site preparation or incorrect utilities</li>
                  <li>Performance issues due to poor water quality, irregular power supply, or improper operation</li>
                  <li>After successful installation and commissioning with customer sign-off</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Refund Process & Timeline</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Request Submission:</strong> Email refund request to aldentechnologiesindia@gmail.com with order details</li>
                <li><strong>Verification:</strong> Our team will verify eligibility within 7 working days</li>
                <li><strong>Approval:</strong> If approved, refund will be processed within 15-21 business days</li>
                <li><strong>Refund Method:</strong> Refunds processed via bank transfer to the original payment account</li>
                <li><strong>GST Credit:</strong> GST input credit reversal documentation required for GST-registered businesses</li>
                <li><strong>Payment Gateway Charges:</strong> Online payment gateway charges (2-3%) are non-refundable</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                Special Services - Cancellation
              </h2>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-4">Consultation Charges:</h3>
              <p className="text-gray-700 mb-4">
                Technical consultation fees are non-refundable once the consultation session is completed or project report is delivered.
              </p>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-4">AMC (Annual Maintenance Contract):</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Cancellation within 30 days: 50% refund of unused portion</li>
                <li>After 30 days: No refund, but contract can be transferred to new equipment owner</li>
              </ul>

              <h3 className="text-lg font-bold text-gray-900 mb-3 mt-4">Spare Parts:</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Unused spare parts in original packaging can be returned within 15 days</li>
                <li>Refund amount: 85% of purchase price (15% restocking fee)</li>
                <li>Custom-made parts are non-returnable</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Force Majeure</h2>
              <p className="text-gray-700">
                Delays or non-performance due to natural disasters, pandemics, government restrictions, war, or other unforeseeable circumstances beyond our control will not qualify for refunds. However, we will work with customers to reschedule delivery.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Dispute Resolution</h2>
              <p className="text-gray-700 mb-4">
                We are committed to resolving all refund disputes amicably. Customers are encouraged to contact us first before escalating to legal channels.
              </p>
            </section>

            <section>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Contact for Refund Queries</h2>
              <div className="bg-orange-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-900">Alden Technologies</p>
                <p className="text-gray-700">KH No 588, Machara Harmada, Jaipur, Rajasthan - 302013</p>
                <p className="text-gray-700">Email: aldentechnologiesindia@gmail.com</p>
                <p className="text-gray-700">Phone: +91-9351292512</p>
                <p className="text-gray-700">GST: 08BUJPR0377M1ZY</p>
                <p className="text-gray-700">Working Hours: Monday to Saturday, 9:00 AM - 6:00 PM</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;